import { FormEvent, useId, useState } from "react";
import { toast } from "sonner";
import { readErrorMessage } from "../lib/http";
import { WorkstationTag } from "../types/clientCompanies";
import GenericTable, { GenericTableColumn } from "./GenericTable";
import Modal from "./Modal";

type WorkstationTagsManagerProps = {
  clientCompanyId: string;
  tags: WorkstationTag[];
  authorizedFetch: (path: string, init?: RequestInit) => Promise<Response>;
  canManageClientCompanies: boolean;
  /** Called after a tag is added, renamed or deleted. */
  onChanged: () => Promise<void>;
};

const TAG_FORM_ID = "workstation-tag-form";

/**
 * Tags a company puts on its workstations so reports can cover a group of
 * workstations at once. Workstations are tagged from the workstation form.
 */
function WorkstationTagsManager({
  clientCompanyId,
  tags,
  authorizedFetch,
  canManageClientCompanies,
  onChanged,
}: WorkstationTagsManagerProps) {
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<WorkstationTag | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const deleteTitleId = useId();

  const endpoint = `/client-companies/${clientCompanyId}/tags`;

  const openAddModal = () => {
    setEditingId(null);
    setName("");
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (tag: WorkstationTag) => {
    setEditingId(tag.id);
    setName(tag.name);
    setFormError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setName("");
    setFormError(null);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    try {
      const response = await authorizedFetch(
        editingId ? `${endpoint}/${editingId}` : endpoint,
        {
          method: editingId ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: name.trim() }),
        },
      );

      if (!response.ok) {
        const fallbackMessage =
          response.status === 409
            ? "A tag with this name already exists for this company."
            : response.status === 400
              ? "Enter a tag name."
              : `Failed to save tag (${response.status}).`;

        throw new Error(await readErrorMessage(response, fallbackMessage));
      }

      toast.success(editingId ? "Tag updated." : "Tag added.");
      closeModal();
      await onChanged();
    } catch (submitError) {
      setFormError(
        submitError instanceof Error
          ? submitError.message
          : "Failed to save tag.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async (tag: WorkstationTag) => {
    setPendingDelete(null);
    setError(null);

    try {
      const response = await authorizedFetch(`${endpoint}/${tag.id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to delete tag (${response.status}).`,
          ),
        );
      }

      toast.success("Tag deleted.");
      await onChanged();
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Failed to delete tag.",
      );
    }
  };

  const columns: GenericTableColumn<WorkstationTag>[] = [
    {
      id: "name",
      header: "Name",
      cell: (tag) => tag.name,
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "actionsColumn",
      cellClassName: "actionsColumn",
      cell: (tag) => (
        <div className="headerActions">
          <button
            className="secondaryButton tableActionButton"
            type="button"
            onClick={() => openEditModal(tag)}
            disabled={!canManageClientCompanies}
          >
            Rename
          </button>
          <button
            className="secondaryButton tableActionButton"
            type="button"
            onClick={() => setPendingDelete(tag)}
            disabled={!canManageClientCompanies}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <section
      className="subCard workstationsSection"
      aria-labelledby="workstation-tags-heading"
    >
      <div className="cardHeaderRow">
        <div>
          <h3 className="subCardTitle" id="workstation-tags-heading">
            Workstation tags
          </h3>
          <p className="hintText sectionHint">
            Group workstations with tags to filter reports by several
            workstations at once.
          </p>
        </div>

        <button
          className="primaryButton"
          type="button"
          onClick={openAddModal}
          disabled={!canManageClientCompanies}
        >
          Add tag
        </button>
      </div>

      {error ? (
        <p className="errorMessage" role="alert">
          {error}
        </p>
      ) : null}

      {tags.length === 0 ? (
        <p className="hintText">No tags have been added yet.</p>
      ) : (
        <GenericTable
          ariaLabel="Workstation tags"
          columns={columns}
          rows={tags}
          rowKey={(tag) => tag.id}
        />
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        titleId="workstation-tag-modal-title"
        title={editingId ? "Rename tag" : "Add tag"}
      >
        <form
          id={TAG_FORM_ID}
          className="modalForm"
          onSubmit={onSubmit}
          noValidate
        >
          <label className="field">
            <span className="fieldLabel">Name</span>
            <input
              className="inputControl"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </label>

          {formError ? (
            <p className="errorMessage" role="alert">
              {formError}
            </p>
          ) : null}

          <div className="modalFooter">
            <button
              className="secondaryButton"
              type="button"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              className="primaryButton"
              type="submit"
              disabled={isSubmitting || !canManageClientCompanies}
            >
              {isSubmitting
                ? "Saving..."
                : editingId
                  ? "Save changes"
                  : "Add tag"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        titleId={deleteTitleId}
        title="Delete tag"
        footer={
          <>
            <button
              type="button"
              className="secondaryButton"
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="primaryButton"
              onClick={() => {
                if (pendingDelete) void onDelete(pendingDelete);
              }}
            >
              Delete
            </button>
          </>
        }
      >
        <p>
          The tag is removed from all workstations. Workstations and their
          employees are not affected.
        </p>
      </Modal>
    </section>
  );
}

export default WorkstationTagsManager;
